/* brand-takeover-fx.js — Seam reveal transition. run(bt) -> { covered, release, done } */
(function () {
  'use strict';


  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function wait(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  function el(tag, cls, css) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (css) n.style.cssText = css;
    return n;
  }

  /* Deterministic noise so a given seed always lays the blobs out the same. */
  /* ---------------------------------------------------------------- shared */

  /* #rgb / #rrggbb / rgb() -> {r,g,b}. Anything else returns null and is left
     alone. */
  function toRgb(css) {
    if (!css) return null;
    var v = String(css).trim();

    var m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v);
    if (m) {
      var h = m[1];
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16)
      };
    }

    m = /^rgba?\(([^)]+)\)$/i.exec(v);
    if (m) {
      var p = m[1].split(',').map(parseFloat);
      if (p.length >= 3) return { r: p[0], g: p[1], b: p[2] };
    }
    return null;
  }

  function luminance(c) {
    return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
  }

  /* These styles draw type and line work in the foreground colour directly on
     the background colour. When a merchant leaves both the same — which is the
     default state of the two colour pickers — every letter, the divider and the
     announcement render invisibly and the transition looks like a blank panel.
     Too little contrast, and the foreground is replaced with black or white. */
  function readableFg(bgCss, fgCss) {
    var bg = toRgb(bgCss);
    var fg = toRgb(fgCss);
    if (!bg) return fgCss || '#ffffff';

    var lb = luminance(bg);
    if (fg) {
      var lf = luminance(fg);
      var ratio = (Math.max(lb, lf) + 0.05) / (Math.min(lb, lf) + 0.05);
      if (ratio >= 1.8) return fgCss;
    }
    return lb > 0.55 ? '#111111' : '#ffffff';
  }

  function host(bt, style) {
    var d = bt.bar.dataset;
    var node = el('div', 'btfx btfx--' + style);
    node.setAttribute('aria-hidden', 'true');

    var bg = d.overlayBg || '#111111';
    var fg = readableFg(bg, d.overlayFg);
    if (fg !== d.overlayFg && window.console) {
      console.info('[brand-takeover] overlay text colour matched the background; using ' + fg + ' instead.');
    }

    node.style.setProperty('--btfx-bg', bg);
    node.style.setProperty('--btfx-fg', fg);
    document.body.appendChild(node);
    return node;
  }

  /* The artwork shown behind a mask/hole: the merchant's overlay image, or the
     brand colour when none is set. Mobile art wins under 768px. */
  function artLayer(bt) {
    var d = bt.bar.dataset;
    var mobile = window.innerWidth < 768;
    var src = (mobile && d.overlayImageMobile) || d.overlayImage || d.overlayImageMobile;
    var layer = el('div', 'btfx__art');
    if (src) layer.style.backgroundImage = 'url("' + src + '")';
    return layer;
  }

  function markLayer(bt) {
    var d = bt.bar.dataset;
    if (!d.overlayMark) return null;
    var img = el('img', 'btfx__mark');
    img.src = d.overlayMark;
    img.alt = '';
    return img;
  }

  /* ------------------------------------------------------------ 1. seam */
  /* Panels meet, a stitch runs along the join, then the seam parts. */

  var SEAM_EASE = {
    expo: 'cubic-bezier(0.16,1,0.3,1)',
    ease: 'cubic-bezier(0.65,0,0.35,1)',
    linear: 'linear',
    accel: 'cubic-bezier(0.7,0,0.84,0)',
    bounce: 'cubic-bezier(0.34,1.4,0.64,1)'
  };
  var SEAM_MIRROR = {
    expo: 'cubic-bezier(0.7,0,0.84,0)',
    ease: 'cubic-bezier(0.65,0,0.35,1)',
    linear: 'linear',
    accel: 'cubic-bezier(0.16,1,0.3,1)',
    bounce: 'cubic-bezier(0.36,-0.4,0.66,-0.4)'
  };

  function seam(bt, node, t) {
    var sd = bt.bar.dataset;
    var easeInKey = SEAM_EASE[sd.seamEaseIn] ? sd.seamEaseIn : 'expo';
    var ease = SEAM_EASE[easeInKey];
    var easeOut = sd.seamEaseOut && sd.seamEaseOut !== 'mirror' && SEAM_EASE[sd.seamEaseOut]
      ? SEAM_EASE[sd.seamEaseOut]
      : SEAM_MIRROR[easeInKey];
    var horiz = sd.seamDir === 'horizontal';
    var axis = horiz ? 'translateY' : 'translateX';
    var showStitch = sd.seamStitch !== 'false';
    if (sd.seamStitchCl) node.style.setProperty('--btfx-stitch', sd.seamStitchCl);
    if (sd.seamStitchW) node.style.setProperty('--btfx-stitch-w', sd.seamStitchW + 'px');
    if (horiz) node.classList.add('btfx--seam-h');

    var left = el('div', 'btfx__half btfx__half--l');
    var right = el('div', 'btfx__half btfx__half--r');
    left.appendChild(artLayer(bt));
    right.appendChild(artLayer(bt));

    var mark = markLayer(bt);
    if (mark) node.appendChild(mark);

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'btfx__stitch');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    /* A slack thread that settles onto the join. */
    path.setAttribute('d', horiz
      ? 'M -2 50 C 20 46, 34 54, 52 50 C 70 46, 84 54, 102 50'
      : 'M 50 -2 C 46 20, 54 34, 50 52 C 46 70, 54 84, 50 102');
    path.setAttribute('class', 'btfx__thread');
    svg.appendChild(path);

    node.appendChild(left);
    node.appendChild(right);
    if (showStitch) node.appendChild(svg);

    left.animate([{ transform: axis + '(-101%)' }, { transform: axis + '(0)' }],
      { duration: t.cover, easing: ease, fill: 'both' });
    right.animate([{ transform: axis + '(101%)' }, { transform: axis + '(0)' }],
      { duration: t.cover, easing: ease, fill: 'both' });

    var len = 220;
    path.style.strokeDasharray = '6 7';
    path.animate(
      [{ strokeDashoffset: len, opacity: 0 }, { strokeDashoffset: len * 0.55, opacity: 1, offset: 0.25 },
       { strokeDashoffset: 0, opacity: 1 }],
      { duration: Math.max(1, t.hold), delay: t.cover, easing: 'linear', fill: 'both' }
    );

    if (mark) {
      mark.animate([{ opacity: 0 }, { opacity: 1 }],
        { duration: t.cover * 0.6, delay: t.cover * 0.4, easing: 'ease-out', fill: 'both' });
    }

    /* The panels only part once the new page is in place, so a slow fetch can
       never open the seam onto the old page. */
    return function reveal() {
      var open = { duration: t.reveal, easing: easeOut, fill: 'forwards' };
      left.animate([{ transform: axis + '(0)' }, { transform: axis + '(-101%)' }], open);
      right.animate([{ transform: axis + '(0)' }, { transform: axis + '(101%)' }], open);
      path.animate([{ opacity: 1 }, { opacity: 0 }],
        { duration: t.reveal * 0.35, easing: 'ease-in', fill: 'forwards' });
      if (mark) {
        mark.animate([{ opacity: 1 }, { opacity: 0 }],
          { duration: t.reveal * 0.4, easing: 'ease-in', fill: 'forwards' });
      }
      return wait(t.reveal + 40).then(function () {
        if (node.parentNode) node.parentNode.removeChild(node);
      });
    };
  }

  /* ------------------------------------------------------------- runner */

  function run(bt) {
    if (reducedMotion()) {
      return { covered: Promise.resolve(), done: Promise.resolve() };
    }

    var sd = bt.bar.dataset;
    var num = function (v, f) { var n = parseInt(v, 10); return isNaN(n) ? f : n; };
    var t = {
      cover: num(sd.seamIn, 900),
      hold: num(sd.seamHold, 350),
      reveal: num(sd.seamOut, 900)
    };

    var node = host(bt, 'seam');
    var reveal = seam(bt, node, t);

    var released = null;
    var done = new Promise(function (resolve) { released = resolve; });
    var safety = setTimeout(function () {
      if (released) { released(reveal()); released = null; }
    }, t.cover + t.hold + 15000);

    return {
      covered: wait(t.cover + t.hold),
      release: function () {
        clearTimeout(safety);
        if (released) { released(reveal()); released = null; }
        return done;
      },
      done: done
    };
  }

  window.BTFX = { run: run };
})();
